import React, { useEffect, useMemo, useRef } from "react";

/**
 * Voxara's android at her desk, page finished, waiting for you to take the file.
 *
 * Folding the arms was tried and read as tangled: two limbs crossing in front of a small torso have
 * to overlap, and at this size the overlap is indistinguishable from a knot. Resting both hands on
 * the desk, one on each side of the page, keeps the arms apart and unambiguous — and sitting at the
 * finished page says "done, waiting for you" better than a pose ever could.
 *
 * The scribe mascot's motion is driven by the audio signal because it exists to prove sound is
 * arriving. Nothing is arriving here, so this one breathes, blinks, and taps a finger.
 */
export default function VoxaraWaiting() {
  const bobRef = useRef(null);
  const headRef = useRef(null);
  const orbRef = useRef(null);
  const eyeLRef = useRef(null);
  const eyeRRef = useRef(null);
  const armLRef = useRef(null);
  const armRRef = useRef(null);
  const handLRef = useRef(null);
  const handRRef = useRef(null);
  const rulesRef = useRef(null);
  const marginRef = useRef(null);
  const inkRef = useRef(null);
  const rafRef = useRef(null);

  // Same sheet corners as the scribe, so the page sits in the same perspective.
  const geom = useMemo(() => {
    const TL = { x: 186, y: 168 };
    const TR = { x: 258, y: 168 };
    const BL = { x: 156, y: 228 };
    const BR = { x: 288, y: 228 };
    const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    const P = (u, v) => lerp(lerp(TL, TR, u), lerp(BL, BR, u), v);
    return { P, rows: [0.22, 0.4, 0.58, 0.76, 0.92], ends: [0.85, 0.7, 0.88, 0.64, 0.8] };
  }, []);

  useEffect(() => {
    const NS = "http://www.w3.org/2000/svg";
    const { P, rows, ends } = geom;

    if (rulesRef.current) {
      rulesRef.current.innerHTML = "";
      rows.forEach((v) => {
        const a = P(0.12, v);
        const b = P(0.92, v);
        const line = document.createElementNS(NS, "line");
        line.setAttribute("x1", a.x.toFixed(1));
        line.setAttribute("y1", a.y.toFixed(1));
        line.setAttribute("x2", b.x.toFixed(1));
        line.setAttribute("y2", b.y.toFixed(1));
        rulesRef.current.appendChild(line);
      });
    }
    if (marginRef.current) {
      const m0 = P(0.18, 0.06);
      const m1 = P(0.18, 0.97);
      marginRef.current.setAttribute("d", `M${m0.x.toFixed(1)} ${m0.y.toFixed(1)} L${m1.x.toFixed(1)} ${m1.y.toFixed(1)}`);
    }

    // The page is already written: she has finished, which is why she is waiting.
    if (inkRef.current) {
      inkRef.current.innerHTML = "";
      rows.forEach((v, i) => {
        let d = "";
        let up = true;
        const steps = 22;
        for (let s = 0; s <= steps; s += 1) {
          const u = 0.22 + (ends[i] - 0.22) * (s / steps);
          const amp = (up ? -1 : 1) * 2.2 * (0.8 + v * 0.5);
          const pt = P(u, v);
          d += `${s === 0 ? "M" : "L"}${pt.x.toFixed(1)} ${(pt.y + amp).toFixed(1)}`;
          up = !up;
        }
        const path = document.createElementNS(NS, "path");
        path.setAttribute("d", d);
        inkRef.current.appendChild(path);
      });
    }

    // Shoulders match the scribe; hands rest on the desk clear of the page on either side.
    const SHL = { x: 196, y: 120 };
    const SHR = { x: 244, y: 120 };
    const HANDL = { x: 148, y: 192 };
    const HANDR = { x: 298, y: 192 };
    const limb = (s, h, bend) => {
      const mx = (s.x + h.x) / 2 + bend;
      const my = (s.y + h.y) / 2 + 8;
      return `M${s.x} ${s.y} Q${mx.toFixed(1)} ${my.toFixed(1)} ${h.x.toFixed(1)} ${h.y.toFixed(1)}`;
    };

    const place = (tapOffset) => {
      const handR = { x: HANDR.x, y: HANDR.y + tapOffset };
      if (armLRef.current) armLRef.current.setAttribute("d", limb(SHL, HANDL, -16));
      if (armRRef.current) armRRef.current.setAttribute("d", limb(SHR, handR, 16));
      if (handLRef.current) {
        handLRef.current.setAttribute("cx", HANDL.x.toFixed(1));
        handLRef.current.setAttribute("cy", HANDL.y.toFixed(1));
      }
      if (handRRef.current) {
        handRRef.current.setAttribute("cx", handR.x.toFixed(1));
        handRRef.current.setAttribute("cy", handR.y.toFixed(1));
      }
    };

    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      place(0);
      return undefined;
    }

    let blinkAt = 0;
    const started = performance.now();

    const frame = (ts) => {
      const seconds = (ts - started) / 1000;

      // Breathing and the head tilt run at different periods so the loop never reads as mechanical.
      if (bobRef.current) {
        bobRef.current.setAttribute("transform", `translate(0 ${(Math.sin(seconds * 1.1) * 1.5).toFixed(2)})`);
      }
      if (headRef.current) {
        headRef.current.setAttribute("transform", `rotate(${(Math.sin(seconds * 0.6) * 2.4).toFixed(2)} 220 80)`);
      }
      if (orbRef.current) orbRef.current.setAttribute("r", (4.8 + Math.sin(seconds * 1.6) * 0.7).toFixed(2));

      // A short burst of finger taps every few seconds: the one gesture that reads as "waiting"
      // rather than "idle" at this size.
      const cycle = seconds % 4.2;
      const tap = cycle < 0.75 ? -3.2 * Math.abs(Math.sin(cycle * Math.PI * 4)) : 0;
      place(tap);

      if (ts > blinkAt) {
        blinkAt = ts + 2600 + Math.random() * 2600;
        [eyeLRef.current, eyeRRef.current].forEach((eye) => {
          if (!eye) return;
          eye.setAttribute("height", "3");
          eye.setAttribute("y", "72");
          window.setTimeout(() => {
            eye.setAttribute("height", "12");
            eye.setAttribute("y", "66");
          }, 130);
        });
      }

      rafRef.current = requestAnimationFrame(frame);
    };

    rafRef.current = requestAnimationFrame(frame);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [geom]);

  return (
    <svg viewBox="112 0 216 246" className="w-full h-auto block" role="img"
         aria-label="Mascota de Voxara sentada en su escritorio, esperando a que descargues el audio">
      <defs>
        <linearGradient id="vwBody" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#22d3ee" /><stop offset="55%" stopColor="#a855f7" /><stop offset="100%" stopColor="#f800f8" />
        </linearGradient>
        <linearGradient id="vwDesk" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#26262c" /><stop offset="100%" stopColor="#151519" />
        </linearGradient>
        <radialGradient id="vwOrb">
          <stop offset="0%" stopColor="#fff" /><stop offset="50%" stopColor="#fbbf24" /><stop offset="100%" stopColor="#f59e0b" />
        </radialGradient>
      </defs>

      <g ref={bobRef}>
        <g ref={headRef}>
          <line x1="220" y1="34" x2="220" y2="18" stroke="#a855f7" strokeWidth="2.5" strokeLinecap="round" />
          <circle ref={orbRef} cx="220" cy="14" r="5" fill="url(#vwOrb)" />
          <rect x="178" y="32" width="84" height="72" rx="26" fill="#17171c" stroke="url(#vwBody)" strokeWidth="2.5" />
          <rect x="190" y="48" width="60" height="46" rx="17" fill="#0a0a0e" />
          <rect ref={eyeLRef} x="204" y="66" width="9" height="12" rx="4.5" fill="#22d3ee" />
          <rect ref={eyeRRef} x="227" y="66" width="9" height="12" rx="4.5" fill="#22d3ee" />
          <path d="M212 84 q8 5 16 0" fill="none" stroke="#22d3ee" strokeWidth="2.2" strokeLinecap="round" opacity=".85" />
          <path d="M174 60 q-2 -32 46 -32 q48 0 46 32" fill="none" stroke="#a855f7" strokeWidth="4.5" strokeLinecap="round" />
          <rect x="166" y="54" width="14" height="28" rx="7" fill="#c026d3" />
          <rect x="260" y="54" width="14" height="28" rx="7" fill="#c026d3" />
        </g>
        <rect x="192" y="102" width="56" height="56" rx="20" fill="#17171c" stroke="url(#vwBody)" strokeWidth="2.5" />
      </g>

      <path d="M34 254 L96 156 L344 156 L406 254 Z" fill="url(#vwDesk)" />
      <line x1="96" y1="156" x2="344" y2="156" stroke="#33333a" strokeWidth="2" />

      <path d="M186 168 L258 168 L288 228 L156 228 Z" fill="#f6f4ef" stroke="#d8d4ca" strokeWidth="1.5" strokeLinejoin="round" />
      <g ref={rulesRef} stroke="#e2ded4" strokeWidth="1" />
      <path ref={marginRef} d="" stroke="#f0b0b0" strokeWidth="1" fill="none" />
      <g ref={inkRef} fill="none" stroke="#2b2b33" strokeWidth="1.9" strokeLinecap="round" />

      {/* Arms drawn after the desk so they rest on it rather than behind it. */}
      <path ref={armLRef} d="" fill="none" stroke="#7c3aed" strokeWidth="7.5" strokeLinecap="round" />
      <path ref={armRRef} d="" fill="none" stroke="#7c3aed" strokeWidth="7.5" strokeLinecap="round" />
      <circle cx="196" cy="120" r="5.5" fill="#8b5cf6" />
      <circle cx="244" cy="120" r="5.5" fill="#8b5cf6" />
      <circle ref={handLRef} cx="148" cy="192" r="7" fill="#a855f7" />
      <circle ref={handRRef} cx="298" cy="192" r="7" fill="#a855f7" />
    </svg>
  );
}
